import {html, nothing} from 'lit';
import type {ClamLoginContext} from './clam-login.types.js';

const iconPerson = html`
    <svg class="field__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="8" r="4"></circle>
        <path d="M4 21v-1a8 8 0 0 1 16 0v1"></path>
    </svg>
`;
const iconLock = html`
    <svg class="field__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="4" y="11" width="16" height="10" rx="2"></rect>
        <path d="M8 11V7a4 4 0 0 1 8 0v4"></path>
    </svg>
`;
const iconShield = html`
    <svg class="field__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"></path>
        <path d="M9.5 12.5l2 2 3.5-4"></path>
    </svg>
`;
const iconMail = html`
    <svg class="field__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2"></rect>
        <path d="m3 7 9 6 9-6"></path>
    </svg>
`;
const iconHash = html`
    <svg class="field__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18"></path>
    </svg>
`;

function input(field: keyof ClamLoginContext, ctx: ClamLoginContext, opts: {type?: string; placeholder: string; autocomplete?: string; inputmode?: string; icon: typeof iconPerson; onInput?: (v: string) => void}) {
    const type = opts.type ?? 'text';
    const value = ctx[field] as string;
    // onInput es ADICIONAL (no exclusivo): siempre actualiza el estado del componente
    // vía onInputChange, y si hay onInput hook (p.ej. para refrescar reglas visuales)
    // se ejecuta después. Antes era OR exclusivo → el campo newPassword nunca se
    // sincronizaba y la validación "Completa todos los campos" disparaba siempre.
    const handler = (e: InputEvent) => {
        const v = (e.target as HTMLInputElement).value;
        ctx.onInputChange(field as string, v);
        opts.onInput?.(v);
    };
    return html`
        <div class="field">
            ${opts.icon}
            <input
                type=${type}
                .value=${value}
                placeholder=${opts.placeholder}
                autocomplete=${opts.autocomplete ?? nothing}
                inputmode=${opts.inputmode ?? nothing}
                @input=${handler}
            />
        </div>
    `;
}

function passwordField(ctx: ClamLoginContext) {
    const type = ctx.showPassword ? 'text' : 'password';
    return html`
        <div class="field">
            ${iconLock}
            <input
                type=${type}
                .value=${ctx.loginPassword}
                placeholder="password"
                autocomplete="current-password"
                @input=${(e: InputEvent) => ctx.onInputChange('loginPassword', (e.target as HTMLInputElement).value)}
            />
            <button type="button" class="eye-btn" @click=${ctx.togglePassword}>
                ${ctx.showPassword ? 'Ocultar' : 'Mostrar'}
            </button>
        </div>
    `;
}

function btn(label: string, ctx: ClamLoginContext) {
    return html`
        <button class="btn" ?disabled=${ctx.loading} @click=${ctx.submit}>
            ${label}
            ${ctx.loading ? html`<span class="spinner"></span>` : nothing}
        </button>
    `;
}

function alert(ctx: ClamLoginContext) {
    if (ctx.error) return html`<div class="alert alert--error">${ctx.error}</div>`;
    if (ctx.success) return html`<div class="alert alert--success">${ctx.success}</div>`;
    return nothing;
}

function rulesList(ctx: ClamLoginContext) {
    if (!ctx.passwordRules.length) return nothing;
    return html`
        <ul class="rules">
            ${ctx.passwordRules.map(
                (r) => html`<li class="rule ${r.passed ? 'rule--pass' : 'rule--fail'}">${r.label}</li>`,
            )}
        </ul>
    `;
}

const formKeydown = (ctx: ClamLoginContext) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
        e.preventDefault();
        ctx.submit();
    }
};

export function renderLogin(ctx: ClamLoginContext) {
    const titleMap: Record<string, string> = {
        login: 'Iniciar Sesión',
        mfaCode: 'Verificación',
        newPassword: 'Nueva Contraseña',
        forgotPassword: 'Recuperar Cuenta',
        confirmReset: 'Confirmar Código',
    };

    return html`
        <div class="login-card">
            <img class="logo" src="/img/branding/logo_icon.png" alt="CLAM Veracruz" />
            <h2 class="login-title">${titleMap[ctx.prompt] ?? 'CLAM Veracruz'}</h2>

            ${alert(ctx)}

            ${ctx.prompt === 'login'
                ? html`
                      <div class="form" @keydown=${formKeydown(ctx)}>
                          ${input('loginEmail', ctx, {
                              type: 'text',
                              placeholder: 'usuario o email',
                              autocomplete: 'username',
                              icon: iconPerson,
                          })}
                          ${passwordField(ctx)}
                          ${btn(ctx.loading ? 'Ingresando...' : 'Iniciar Sesión', ctx)}
                      </div>
                      <p class="text">
                          ¿No eres socio CLAM?
                          <a href="https://clamveracruz.com/registro" target="_blank" rel="noopener">Regístrate</a>
                      </p>
                      <p class="text">
                          <button type="button" class="link-btn" @click=${ctx.goToReset}>Olvidé mi contraseña</button>
                      </p>
                  `
                : nothing}

            ${ctx.prompt === 'mfaCode'
                ? html`
                      <p class="info">Ingresa el código de tu app autenticadora (TOTP).</p>
                      <div class="form" @keydown=${formKeydown(ctx)}>
                          ${input('mfaCode', ctx, {
                              placeholder: '123456',
                              autocomplete: 'one-time-code',
                              inputmode: 'numeric',
                              icon: iconShield,
                          })}
                          ${btn(ctx.loading ? 'Verificando...' : 'Verificar', ctx)}
                      </div>
                      <p class="text"><button type="button" class="link-btn" @click=${ctx.goToLogin}>Volver al login</button></p>
                  `
                : nothing}

            ${ctx.prompt === 'newPassword'
                ? html`
                      <p class="info">Debes cambiar tu contraseña para continuar.</p>
                      <div class="form" @keydown=${formKeydown(ctx)}>
                          ${ctx.missingAttributes.includes('name')
                              ? input('firstName', ctx, {
                                    type: 'text',
                                    placeholder: 'nombre (requerido)',
                                    autocomplete: 'given-name',
                                    icon: iconPerson,
                                })
                              : nothing}
                          ${ctx.missingAttributes.includes('family_name')
                              ? input('lastName', ctx, {
                                    type: 'text',
                                    placeholder: 'apellido (requerido)',
                                    autocomplete: 'family-name',
                                    icon: iconPerson,
                                })
                              : nothing}
                          ${input('newPassword', ctx, {
                              type: 'password',
                              placeholder: 'nueva contraseña',
                              autocomplete: 'new-password',
                              icon: iconLock,
                              onInput: (v) => ctx.onNewPasswordInput(v),
                          })}
                          ${input('newPasswordConfirm', ctx, {
                              type: 'password',
                              placeholder: 'confirmar contraseña',
                              autocomplete: 'new-password',
                              icon: iconLock,
                          })}
                          ${rulesList(ctx)}
                          ${btn(ctx.loading ? 'Cambiando...' : 'Cambiar Contraseña', ctx)}
                      </div>
                  `
                : nothing}

            ${ctx.prompt === 'forgotPassword'
                ? html`
                      <p class="info">Ingresa tu email para recibir un código de recuperación.</p>
                      <div class="form" @keydown=${formKeydown(ctx)}>
                          ${input('forgotEmail', ctx, {
                              type: 'email',
                              placeholder: 'email',
                              autocomplete: 'email',
                              icon: iconMail,
                          })}
                          ${btn(ctx.loading ? 'Enviando...' : 'Enviar Código', ctx)}
                      </div>
                      <p class="text"><button type="button" class="link-btn" @click=${ctx.goToLogin}>Volver al login</button></p>
                  `
                : nothing}

            ${ctx.prompt === 'confirmReset'
                ? html`
                      <p class="info">Ingresa el código recibido y tu nueva contraseña.</p>
                      <div class="form" @keydown=${formKeydown(ctx)}>
                          ${input('resetCode', ctx, {
                              placeholder: 'código',
                              inputmode: 'numeric',
                              icon: iconHash,
                          })}
                          ${input('resetPassword', ctx, {
                              type: 'password',
                              placeholder: 'nueva contraseña',
                              autocomplete: 'new-password',
                              icon: iconLock,
                          })}
                          ${btn(ctx.loading ? 'Cambiando...' : 'Cambiar Contraseña', ctx)}
                      </div>
                      <p class="text">
                          <button type="button" class="link-btn" @click=${ctx.goToReset}>No recibí el código</button> ·
                          <button type="button" class="link-btn" @click=${ctx.goToLogin}>Volver al login</button>
                      </p>
                  `
                : nothing}
        </div>
    `;
}
